import { Controller, useFormContext } from "react-hook-form";

import {
  Label,
  RadioCardItem,
  RadioCards,
  RadioCardTitle,
  Text,
} from "@flare/ui/components";

import type { FunnelAnswers } from "@/modules/v2/funnel/config";
import { i18n } from "@/services/locale/i18n";

import { SUB_PRACTICE_TO_SERVICE_TYPE_MAP } from "../calculator.consts";
import { sendCalculatorAnswerBiEvent } from "../calculator.utils";

const { t } = i18n;

const SUB_PRACTICES_RADIO_CARDS_ITEMS: {
  label: string;
  value: FunnelAnswers["subPractice"];
}[] = [
  {
    value: "divorce",
    label: t("COST_CALCULATOR_QUESTION_SUB_PRACTICE_DIVORCE"),
  },
  {
    value: "child-custody",
    label: t("COST_CALCULATOR_QUESTION_SUB_PRACTICE_CHILD_CUSTODY"),
  },
  {
    value: "child-support",
    label: t("COST_CALCULATOR_QUESTION_SUB_PRACTICE_CHILD_SUPPORT"),
  },
  {
    value: "else",
    label: t("COST_CALCULATOR_QUESTION_SUB_PRACTICE_SOMETHING_ELSE"),
  },
];

export function CalculatorAboutYou() {
  const { control, watch } = useFormContext();

  const subPractice: FunnelAnswers["subPractice"] = watch("subPractice");
  const serviceTypes =
    SUB_PRACTICE_TO_SERVICE_TYPE_MAP[
      subPractice as keyof typeof SUB_PRACTICE_TO_SERVICE_TYPE_MAP
    ];
  const showServiceTypes = subPractice !== "else" && serviceTypes?.length > 1;

  return (
    <div className="flex w-full flex-col gap-sp-6">
      <Controller
        control={control}
        name="subPractice"
        render={({ field, fieldState }) => (
          <div className="flex flex-col gap-sp-2">
            <Label htmlFor="subPractice">
              {t("COST_CALCULATOR_QUESTION_SUB_PRACTICE_LABEL")}
            </Label>
            <RadioCards
              defaultValue={field.value}
              onValueChange={(value) => {
                field.onChange(value);
                sendCalculatorAnswerBiEvent({
                  key: "subPractice",
                  value,
                  text: t("COST_CALCULATOR_QUESTION_SUB_PRACTICE_LABEL"),
                  type: "click",
                });
              }}
              className="flex flex-wrap gap-sp-3 overflow-x-hidden"
            >
              {SUB_PRACTICES_RADIO_CARDS_ITEMS.map((item) => (
                <RadioCardItem
                  key={item.value}
                  value={item.value!}
                  className="md:max-w-[273px]"
                >
                  <RadioCardTitle>{item.label}</RadioCardTitle>
                </RadioCardItem>
              ))}
            </RadioCards>
            {fieldState.error && (
              <Text variant="caption" textColor="danger-highlight">
                {fieldState.error.message}
              </Text>
            )}
          </div>
        )}
      />
      {showServiceTypes && (
        <Controller
          control={control}
          name="serviceType"
          render={({ field, fieldState }) => (
            <div className="flex flex-col gap-sp-2">
              <Label htmlFor="serviceType">
                {t("COST_CALCULATOR_QUESTION_SERVICE_TYPE_LABEL")}
              </Label>
              <RadioCards
                defaultValue={field.value}
                onValueChange={(value) => {
                  field.onChange(value);
                  sendCalculatorAnswerBiEvent({
                    key: "serviceType",
                    value,
                    text: t("COST_CALCULATOR_QUESTION_SERVICE_TYPE_LABEL"),
                    type: "click",
                  });
                }}
                className="flex flex-wrap gap-sp-3 overflow-x-hidden"
              >
                {serviceTypes.map((item) => (
                  <RadioCardItem
                    key={item.value}
                    value={item.value}
                    className="md:max-w-[273px]"
                  >
                    <RadioCardTitle>{item.label}</RadioCardTitle>
                  </RadioCardItem>
                ))}
              </RadioCards>
              {fieldState.error && (
                <Text variant="caption" textColor="danger-highlight">
                  {fieldState.error.message}
                </Text>
              )}
            </div>
          )}
        />
      )}

      <Controller
        control={control}
        name="personalStatus"
        render={({ field, fieldState }) => (
          <div className="flex flex-col gap-sp-2">
            <Label htmlFor="personalStatus">
              {t("COST_CALCULATOR_QUESTION_PERSONAL_STATUS_LABEL")}
            </Label>
            <RadioCards
              defaultValue=""
              onValueChange={(value) => {
                field.onChange(value);
                sendCalculatorAnswerBiEvent({
                  key: "personalStatus",
                  value,
                  text: t("COST_CALCULATOR_QUESTION_PERSONAL_STATUS_LABEL"),
                  type: "click",
                });
              }}
              className="flex gap-sp-3"
            >
              <RadioCardItem value="married">
                <RadioCardTitle>
                  {t("COST_CALCULATOR_QUESTION_LABEL_YES")}
                </RadioCardTitle>
              </RadioCardItem>
              <RadioCardItem value="single">
                <RadioCardTitle>
                  {t("COST_CALCULATOR_QUESTION_LABEL_NO")}
                </RadioCardTitle>
              </RadioCardItem>
            </RadioCards>
            {fieldState.error && (
              <Text variant="caption" textColor="danger-highlight">
                {fieldState.error.message}
              </Text>
            )}
          </div>
        )}
      />
      <Controller
        control={control}
        name="hasMinorChildren"
        render={({ field, fieldState }) => (
          <div className="flex flex-col gap-sp-2">
            <Label htmlFor="hasMinorChildren">
              {t("COST_CALCULATOR_QUESTION_CHILDREN_UNDER_18_LABEL")}
            </Label>
            <RadioCards
              defaultValue=""
              onValueChange={(value) => {
                field.onChange(value);
                sendCalculatorAnswerBiEvent({
                  key: "hasMinorChildren",
                  value,
                  text: t("COST_CALCULATOR_QUESTION_CHILDREN_UNDER_18_LABEL"),
                  type: "click",
                });
              }}
              className="flex gap-sp-3"
            >
              <RadioCardItem value="true">
                <RadioCardTitle>
                  {t("COST_CALCULATOR_QUESTION_LABEL_YES")}
                </RadioCardTitle>
              </RadioCardItem>
              <RadioCardItem value="false">
                <RadioCardTitle>
                  {t("COST_CALCULATOR_QUESTION_LABEL_NO")}
                </RadioCardTitle>
              </RadioCardItem>
            </RadioCards>
            {fieldState.error && (
              <Text variant="caption" textColor="danger-highlight">
                {fieldState.error.message}
              </Text>
            )}
          </div>
        )}
      />
    </div>
  );
}
